// ENGLISH fragment of the « rules » set: Game rules (/regles-de-jeu, the target
// of the QR code on the box), Order confirmed (/success) and the 404 page.
// CONSISTENCY: questions 5, 7 and 11 are repeated verbatim in en/experience.js
// (demo.question.*) — any edit here must be mirrored there. The demo tie-breaker
// (demo.subsidiaire.question) is DELIBERATELY a different question, so the home
// page never spoils the real one.
// TO CHECK with the printer: the box carries a bilingual French / English
// summary; if a full English guide ships in the box, these 11 questions must
// use its exact wording.
// NB: the answer to the tie-breaker question deliberately has NO key:
// it must live only in GameRules.jsx, behind the « parents only » click.
export default {
  // --- Game rules: SEO ---
  'regles.seo.titre': 'Joyful Reveal game rules',
  'regles.seo.description':
    'The full rules of the Joyful Reveal gender reveal game: form two teams, answer the 11 questions, piece the puzzle together and scratch the final piece that reveals the baby’s sex.',

  // --- Game rules: header ---
  'regles.entete.titre': 'The game rules',
  'regles.entete.introAvant':
    'Two teams, 11 questions, a puzzle to piece together and a final piece to scratch. Allow around',
  'regles.entete.introDuree': '20 minutes',
  'regles.entete.introEntre': ', for',
  'regles.entete.introJoueurs': '2 to 10 players',

  // --- Game rules: warning for guests ---
  'regles.invite.titre': '🤫 Have you been invited to a party?',
  'regles.invite.destinee': 'This page is meant for the',
  'regles.invite.parents': 'parents-to-be',
  'regles.invite.suite':
    'who are getting the game ready. If you are a player, close it now: you would be spoiling your own surprise!',

  // --- Game rules: what is in the kit ---
  'regles.kit.titre': '🎁 What your kit contains',
  'regles.kit.sachetAvant': 'One main bag holding',
  'regles.kit.sachetPieces': '11 puzzle pieces',
  'regles.kit.grattoirAvant': 'and',
  'regles.kit.grattoirPiece': 'one unique piece to scratch',
  'regles.kit.grattoirFin': '— the one that reveals everything.',
  'regles.kit.premium':
    'The Premium Pack adds the two team pouches, the questionnaire printed on 400 g card and the keepsake envelope to slip behind a picture frame after the party.',

  // --- Game rules: the 4 steps ---
  'regles.etape1.titre': 'Before the guests arrive',
  'regles.etape1.alt':
    'The contents of the Joyful Reveal game laid out: the box, the quiz sheet and the two pouches',
  'regles.etape1.p1':
    'The parents-to-be choose the 11 questions the players will have to answer. Every correct answer wins a puzzle piece.',
  'regles.etape1.p2':
    'The answer is always “mum” or “dad” — and it is all meant with a big wink, of course!',
  'regles.etape1.p3':
    'You can use our ready-made list (further down) or invent your own with the blank template.',

  'regles.etape2.titre': 'Form the two teams',
  'regles.etape2.alt': 'The two cotton pouches from the game, labelled Team 1 and Team 2',
  'regles.etape2.p1': 'Split your guests into two teams — for example:',
  'regles.etape2.equipe1Label': 'Team 1',
  'regles.etape2.equipe1Texte': 'those who think the baby will be a girl 💗',
  'regles.etape2.equipe2Label': 'Team 2',
  'regles.etape2.equipe2Texte': 'those who think the baby will be a boy 💙',

  'regles.etape3.titre': 'The game: winning the 11 pieces',
  'regles.etape3.alt': 'Back of the Joyful Reveal box summarising the 4 steps of the game',
  'regles.etape3.p1':
    'The parents-to-be ask the questions one by one. The first team to give the right answer wins a puzzle piece.',
  'regles.etape3.p2': 'Carry on like this for all 11 standard pieces.',
  'regles.etape3.p3':
    'Once the 11 pieces have been won, the two teams work together to put the puzzle together… and only one piece is missing!',

  'regles.etape4.titre': 'The last piece: the big reveal',
  'regles.etape4.alt': 'The Joyful Reveal game box with the puzzle piece to scratch',
  'regles.etape4.p1':
    'Only the team that has won the most pieces may play this final round — and this time every player plays for themselves.',
  'regles.etape4.p2':
    'The parents-to-be then ask the tie-breaker question. The player whose guess comes closest wins the last piece… and the privilege of scratching it!',

  // --- Game rules: tie-breaker question (the answer stays in the JSX) ---
  'regles.subsidiaire.surtitre': 'The tie-breaker question',
  'regles.subsidiaire.question':
    '“On average, how many disposable nappies do new parents use during their baby’s first year?”',
  'regles.subsidiaire.reponseLabel': 'Answer:',
  'regles.subsidiaire.bouton': '👀 Show the answer (parents only)',
  'regles.subsidiaire.libre':
    'It’s our favourite question — but feel free to invent your own: it just needs an answer that can be guessed as a number.',

  // --- Game rules: the tip ---
  'regles.conseil.surtitre': '💡 A little tip',
  'regles.conseil.titre': 'Resist the temptation to warn your guests!',
  'regles.conseil.texte':
    'Just picture their reaction when they discover the game and realise they are about to live a one-of-a-kind moment, full of suspense. Seeing their faces light up is really worth it. So hold on, and do not give your secret away!',

  // --- Game rules: the 11 questions ---
  'regles.questions.titre': '📝 The 11 ready-to-play questions',
  'regles.questions.imprimer': '🖨️ Print',
  'regles.questions.q1': 'Who is the better cook when it comes to making baby purées?',
  'regles.questions.q2': 'Who is more likely to panic during the birth?',
  'regles.questions.q3': 'Who will more easily remember the baby’s birth length and weight?',
  'regles.questions.q4': 'Who will struggle the most to change the baby’s nappies?',
  'regles.questions.q5': 'Who will sing the lullabies disastrously?',
  'regles.questions.q6': 'Who won’t resist buying cute but useless clothes for the baby?',
  'regles.questions.q7': 'Who will cry at children’s films?',
  'regles.questions.q8': 'Who will pull the best faces to make the baby laugh?',
  'regles.questions.q9':
    'Who is most likely not to work out how to put the baby’s clothes on?',
  'regles.questions.q10': 'Who is the better storyteller for sending the baby off to sleep?',
  'regles.questions.q11':
    'Who might clumsily wake the baby by stepping on a noisy toy?',
  'regles.questions.persoAvant':
    'Fancy personalising it? Feel free to swap these questions for your own — what matters is that the answer is always',
  'regles.questions.persoMaman': '“mum”',
  'regles.questions.persoOu': 'or',
  'regles.questions.persoPapa': '“dad”',
  'regles.questions.persoFin': '. The Premium Pack contains a blank template made for exactly that.',

  // --- Game rules: final CTA ---
  'regles.cta.titre': 'Not got the game yet?',
  'regles.cta.texte': 'Have fun — and congratulations again to you, parents-to-be! 🎉',
  'regles.cta.bouton': '🛒 Discover the game',

  // --- 404 page (NotFound.jsx), filed under « regles » for want of its own set ---
  'regles.introuvable.seoTitre': '404 - Page not found',
  'regles.introuvable.seoDescription':
    'The page you are looking for does not exist. Go back to the home page or explore our shop.',
  'regles.introuvable.titre': '404 - Oops!',
  'regles.introuvable.sousTitre': 'We could not find the page you are looking for.',
  'regles.introuvable.texte':
    'The page may have been deleted or moved, or the URL may be incorrect.',
  'regles.introuvable.boutonAccueil': '🏠 Back to the home page',
  'regles.introuvable.boutonBoutique': '🛍️ Go to the shop',
  'regles.introuvable.aide': 'Need help?',
  'regles.introuvable.aideLien': 'Contact us',

  // --- Order confirmed (Success.jsx) ---
  'succes.chargement.titre': 'Finalising your order...',
  'succes.chargement.texte': 'We are fetching your tracking number, it will not take long.',
  'succes.entete.titre': 'Order confirmed!',
  'succes.entete.numeroLabel': 'Order number:',
  'succes.entete.merci':
    'Thank you for your purchase! You will receive a confirmation email with the details of your order and your invoice.',
  'succes.suivi.titre': 'Tracking your parcel',
  'succes.suivi.etiquette': 'Your shipping label has been created!',
  'succes.suivi.numeroLabel': 'Tracking no.:',
  'succes.suivi.bouton': '🚚 Track my parcel',
  'succes.suivi.astuce': '💡 You can also find this link in your profile → My orders',
  'succes.attente.texte':
    'Your order is being processed. The tracking number will be available in your customer area shortly.',
  // Downloadable rulebook. It reveals the tie-breaker answer: the warning is
  // not decoration.
  // Affiché quand la session de paiement n'est plus lisible (lien rouvert
  // des semaines plus tard) : la page restait alors amputée EN SILENCE —
  // ni numéro de commande, ni guide, ni facture, et aucune explication.
  'succes.introuvable.texte':
    "Your payment went through. We simply can't display this order's details here — that happens when this link is reopened later. Your confirmation e-mail contains your order number and your invoice; write to us if you can't find it.",
  'succes.guide.titre': 'Your game guide',
  'succes.guide.texte':
    'The complete booklet, with the 11 ready-to-play questions and the blank template for writing your own. Keep it to hand on the big day.',
  'succes.guide.bouton': '📥 Download the guide (PDF)',
  'succes.guide.avertissement':
    '⚠️ For the parents-to-be only: it gives away the answer to the tie-breaker question.',

  'succes.questionnaire.titre': 'Your keepsake questionnaire',
  'succes.questionnaire.texte':
    'The print-ready quiz sheet, in the same format as the card in the kit: the 11 questions and the team columns. Tick the box for a blank version to fill in with your own questions.',
  'succes.questionnaire.vierge': 'Blank version (to write your own questions)',
  'succes.questionnaire.bouton': '📥 Download the questionnaire (PDF)',
  'succes.questionnaire.avertissement':
    '⚠️ For the parents-to-be only: its back gives away the answer to the tie-breaker question.',

  "succes.facture.titre": "Your invoice",
  "succes.facture.texte":
    "The invoice for your order (in French) is ready. Stripe also emails you a payment receipt.",
  "succes.facture.bouton": "🧾 View and print the invoice",
  'succes.avis.titre': 'Your review counts!',
  'succes.avis.texte': 'Share your experience and help other customers discover our products.',
  'succes.avis.bouton': '✍️ Leave a review',
  'succes.actions.commandes': '📋 See my orders',
  'succes.actions.boutique': '🎮 Continue shopping',
};
